import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { getEventByIdAdmin } from "../../api/plan";
import { getUserMeEvents } from "../../api/user/me";
import DashboardLayout from "./components/Layout";
import { photoUrl } from "../../utils/photo";
import useRole, { decodeAuth, hasAnyEventRole } from "./components/useRole";
import VoucherStatsComponent from "./VoucherStatsComponent";
import TicketStatsComponent from "./TicketStatsComponent";

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString("az-AZ", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const EventCard = ({ event }) => (
  <Link
    to={`/dashboard/${event._id}`}
    className="group bg-white rounded-2xl overflow-hidden border border-surface-200 hover:border-accent-300 hover:shadow-xl hover:shadow-surface-200/60 transition-all duration-300"
  >
    <div className="relative h-40 bg-surface-100 overflow-hidden">
      {event.photo ? (
        <img
          loading="lazy"
          src={photoUrl(event.photo, 640)}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-500 to-pink-500 text-white text-4xl font-bold">
          {event.title?.[0] || "🎟️"}
        </div>
      )}
      <span
        className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
          event.isActive
            ? "bg-emerald-500 text-white"
            : "bg-surface-900/70 text-surface-200"
        }`}
      >
        {event.isActive ? "Aktiv" : "Deaktiv"}
      </span>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-surface-900 truncate group-hover:text-accent-600 transition-colors">
        {event.title}
      </h3>
      <div className="mt-2 space-y-1 text-sm text-surface-500">
        {event.place && (
          <div className="flex items-center gap-1.5 truncate">📍 {event.place}</div>
        )}
        {formatDate(event.date) && (
          <div className="flex items-center gap-1.5">🗓️ {formatDate(event.date)}</div>
        )}
      </div>
    </div>
  </Link>
);

const EventsGrid = () => {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    getUserMeEvents().then((res) => setEvents(res?.data || []));
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-surface-900">
            Tədbirləriniz
          </h1>
          <p className="text-surface-500 mt-1">
            İdarə etmək üçün tədbir seçin və ya yenisini yaradın.
          </p>
        </div>
        <Link
          to="/dashboard/create-event"
          className="inline-flex items-center gap-2 bg-accent-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-accent-700 transition-colors duration-200"
        >
          ➕ Yeni Tədbir
        </Link>
      </div>

      {events === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-surface-200 overflow-hidden animate-pulse">
              <div className="h-40 bg-surface-200" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-200 rounded w-3/4" />
                <div className="h-3 bg-surface-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-surface-300 p-12 text-center">
          <div className="text-5xl mb-4">🎪</div>
          <h3 className="text-lg font-bold text-surface-900 mb-2">Hələ tədbiriniz yoxdur</h3>
          <p className="text-surface-500 mb-6">İlk tədbirinizi yaradın və bilet satışına başlayın.</p>
          <Link
            to="/dashboard/create-event"
            className="inline-flex items-center gap-2 bg-accent-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-accent-700 transition-colors duration-200"
          >
            ➕ Yeni Tədbir yarat
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((event) => (
            <EventCard key={event._id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { "*": wildcard } = useParams();
  const id = wildcard?.split("/")?.[0];

  const [eventData, setEventData] = useState(null);

  const decoded = decodeAuth();

  // the role on this event; the same account may hold another on the next one
  const { seesStats } = useRole(id);

  useEffect(() => {
    if (!hasAnyEventRole(decoded)) {
      window.location.replace("/");
    }
  }, [decoded]);

  const fetchEventData = async (id) => {
    const response = await getEventByIdAdmin(id);
    setEventData(response?.data?.data);
  };

  useEffect(() => {
    if (id) {
      fetchEventData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <DashboardLayout id={id}>
      {id ? (
        <div>
          <div className="bg-white p-6 rounded-2xl border border-surface-200 shadow-sm">
            <h2 className="text-2xl lg:text-3xl font-extrabold mb-3 leading-tight text-surface-900">
              {eventData?.title}
            </h2>
            <div className="text-sm text-surface-500 space-y-1">
              <div>🗓️ Tarix: {formatDate(eventData?.date) || eventData?.eventDate}</div>
              <div>📍 Məkan: {eventData?.place}</div>
            </div>
          </div>

          {seesStats ? (
            <>
              <h1 className="text-xl font-bold mt-8 mb-4 text-surface-900">📊 Statistika</h1>

              <TicketStatsComponent eventId={id} />

              <div className="mt-8">
                <VoucherStatsComponent eventId={id} />
              </div>
            </>
          ) : (
            // Door staff: the event, and the two things they came here to do
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <Link
                to="/scan"
                state={{ from: `/dashboard/${id}` }}
                className="bg-surface-900 text-white rounded-2xl px-6 py-5 font-semibold hover:bg-surface-800 transition"
              >
                📷 Bilet yoxla
              </Link>
              <Link
                to={`/dashboard/${id}/tickets`}
                className="bg-white border border-surface-200 rounded-2xl px-6 py-5 font-semibold text-surface-900 hover:border-surface-300 transition"
              >
                📄 Biletlərin siyahısı
              </Link>
            </div>
          )}
        </div>
      ) : (
        <EventsGrid />
      )}
    </DashboardLayout>
  );
};

export default DashboardPage;
